// Global quick-create keyboard shortcuts — n = new invoice, q = new quote, g = dashboard. Ported
// from invoiceTest's bindGlobalShortcuts (invoiceTest/src/main.js lines 4086-4098), A4 of the
// documents parity sweep (#41,42,48). ONE listener, registered once at app boot (see src/main.js),
// deliberately separate from the list-scoped j/k/Enter/x/? layer that lives in tenant/documents.js
// — that layer only runs while a documents list is mounted, and only ever handles j/k/Enter/x/?;
// this one is global, always live, and only ever handles n/q/g, so the two layers never fight over
// a key.
//
// companyId is never cached: it's read fresh off location.pathname on every keypress, because this
// handler outlives any one screen and the app is a path-based SPA (`/co/{id}/...`, main.js) — the
// URL is always the single source of truth for "which company is the user looking at right now".
// If the current path isn't under /co/{id}/… (admin console, /login, /home, a public /d/{token} or
// /portal/{token} page…) there is no company in context, so n/q/g are silently ignored.
import { navigate } from './t4.js';
import { isAuthed } from './auth.js';

function currentCompanyId(){
  const path = typeof location !== 'undefined' ? location.pathname : '';
  const m = path.match(/^\/co\/([^/?]+)/);
  return m ? m[1] : null;
}

// Same shape as tenant/documents.js's own kbGuard — reads document.activeElement rather than
// e.target, so it behaves identically no matter how the keydown reaches us (a real keypress bubbles
// from the focused element anyway, but a dispatched/synthetic event isn't guaranteed to).
function blockedByFocusOrModal(){
  const ae = document.activeElement;
  const tag = (ae && ae.tagName || '').toLowerCase();
  if(tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if(ae && ae.isContentEditable) return true;
  if(document.querySelector('.modal-overlay')) return true;
  return false;
}

export function globalKbHandler(e){
  if(e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
  if(blockedByFocusOrModal()) return;
  if(!isAuthed()) return;                  // not on an authed app route
  const companyId = currentCompanyId();
  if(!companyId) return;                   // not on a /co/{id}/… screen — no company in context
  const k = (e.key || '').toLowerCase();
  if(k === 'n'){ e.preventDefault(); navigate('/co/' + companyId + '/invoices/new'); }
  else if(k === 'q'){ e.preventDefault(); navigate('/co/' + companyId + '/quotes/new'); }
  else if(k === 'g'){ e.preventDefault(); navigate('/co/' + companyId + '/dashboard'); }
}

// Stash-and-replace so only ONE listener is ever live (the house pattern — see documents.js's own
// window.__docKbHandler): re-calling this (HMR, or a stray second boot) must never leave two
// handlers attached and firing double navigations.
export function initGlobalShortcuts(){
  if(window.__globalKbHandler) document.removeEventListener('keydown', window.__globalKbHandler);
  window.__globalKbHandler = globalKbHandler;
  document.addEventListener('keydown', globalKbHandler);
}
